import React from 'react';
import { useDisclosure } from '@withjoy/joykit';

import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import type { CurrencyOption, DropdownPositionStyle } from './CurrencySelectorModal';
import CurrencySelectorModal, { CurrencySelectorTrigger } from './CurrencySelectorModal';

type CurrencySelectorProps = Readonly<{
  isEnabled: boolean;
  supportedCurrencies?: ReadonlyArray<CurrencyOption>;
  dropdownPositionStyle?: DropdownPositionStyle;
  /**
   * Show an inert trigger in place of the picker, e.g. while `supportedCurrencies` loads. The modal is not
   * mounted, so its reset-to-USD check never runs against an incomplete list.
   */
  isLoading?: boolean;
  /** The current currency, when the caller resolves it differently from the geo locale (e.g. stationery). */
  currency?: CurrencyCodeType;
}>;

export const CurrencySelector: React.FC<CurrencySelectorProps> = ({ isEnabled, supportedCurrencies, dropdownPositionStyle, isLoading, currency }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (!isEnabled) return null;

  if (isLoading) {
    // Same plain wrapper the modal puts around its trigger, so the layout doesn't shift when the list arrives.
    return (
      <div>
        <CurrencySelectorTrigger
          isOpen={false}
          onClick={() => undefined}
          isDisabled={true}
          currency={currency}
        />
      </div>
    );
  }

  const handleTriggerClick = () => {
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  };

  return (
    <CurrencySelectorModal
      isOpen={isOpen}
      onClose={onClose}
      supportedCurrencies={supportedCurrencies}
      dropdownPositionStyle={dropdownPositionStyle}
      currency={currency}
      trigger={
        <CurrencySelectorTrigger
          isOpen={isOpen}
          onClick={handleTriggerClick}
          currency={currency}
        />
      }
    />
  );
};
